import React, { useMemo } from "react";
import { ChartModalShell, TopBars, CwEvents } from "../screens/logs/LogChartParts";
import { topBy, levelTotals, LEVEL_STATUS } from "../utility/logAnalytics";

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// LogErrorsChartModal (#247) — repurposed from the dormant "Query Performance"
// chart. Ranks the most frequent message shapes in the window, with the
// error/warn headline up top so a spike in failures is obvious. Single magnitude
// axis; horizontal top-N bars.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

function Headline({ totals }) {
  return (
    <div className="flex gap-4 text-sm mb-2">
      <span style={{ color: LEVEL_STATUS.error.color, fontWeight: 700 }}>
        {totals.error} error{totals.error === 1 ? "" : "s"}
      </span>
      <span style={{ color: LEVEL_STATUS.warn.color, fontWeight: 700 }}>
        {totals.warn} warn{totals.warn === 1 ? "" : "s"}
      </span>
      <span className="text-skin-link">{totals.info} info / event</span>
    </div>
  );
}

function TopChart({ events, loading, note }) {
  const totals = useMemo(() => levelTotals(events, (e) => e.level), [events]);
  // Group by the first 60 chars of the message so the same log line with
  // differing ids/timings collapses into one ranked shape.
  const top = useMemo(
    () => topBy(events, (e) => (e.text || "").slice(0, 60), 12),
    [events]
  );
  if (loading) return <div className="text-skin-link italic p-4">Loading…</div>;
  return (
    <div className="flex flex-col h-full min-h-0">
      <Headline totals={totals} />
      {note && <div className="text-xs text-skin-link mb-1">{note}</div>}
      <div className="text-xs text-skin-link mb-1">
        Most frequent message shapes in this window
      </div>
      <div className="flex-1 min-h-0">
        {top.length ? (
          <TopBars data={top} color="#2563eb" />
        ) : (
          <div className="text-skin-link italic p-4">No events in this window.</div>
        )}
      </div>
    </div>
  );
}

export const LogErrorsChartModal = ({ isOpen, onClose, source, cwWindowId }) => {
  // Re-anchor the time window each time the modal opens (intentional isOpen dep).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const anchorMs = useMemo(() => Date.now(), [isOpen]);
  if (!isOpen) return null;

  return (
    <ChartModalShell
      title="⚡ Top events & errors"
      subtitle={source?.label || ""}
      onClose={onClose}
    >
      <CwEvents
        groupId={source?.cwGroupId}
        windowId={cwWindowId}
        anchorMs={anchorMs}
        render={(events, loading, truncated, error) => (
          <TopChart
            events={events}
            loading={loading}
            note={
              error
                ? `CloudWatch error: ${error}`
                : truncated
                ? "This window holds more events than the chart fetches. Showing the OLDEST ones — narrow the window to see current activity."
                : ""
            }
          />
        )}
      />
    </ChartModalShell>
  );
};
